import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useI18N } from "@src/language/language-context";
import { DropDownBlackBackground } from "@src/components/atoms/dropdown-black-background/dropdown-black-background";
import { ToggleButton } from "@src/components/atoms/toggle-button/toggle-button";
import { CustomTooltip } from "@src/components/atoms/custom-tooltip/custom-tooltip";
import requestRemoteUserIcon from "@src/assets/svg/request-remote-access-user-green-icon.svg";
import {
  NoVncVersion,
  NOVNC_VERSIONS,
  VncSettings,
} from "@src/cloud-connect/vnc/utils/novnc-loader";
import { formatDuration } from "@src/cloud-connect/vpn-manager/utils/format-duration-string";
import styles from "./vnc-settings-modal.module.scss";

interface VncSettingsModalProps {
  anchorRef: React.RefObject<HTMLElement>;
  remainingSeconds: number;
  totalSeconds: number;
  onEnterScreensharing: (settings: VncSettings) => void;
  onDisconnect: () => void;
  onClose: () => void;
  disconnecting?: boolean;
}

const cssClassName = "vnc-settings-modal";

const DEFAULT_VERSION: NoVncVersion = "1.3.0";

export const VncSettingsModal = ({
  anchorRef,
  remainingSeconds,
  totalSeconds,
  onEnterScreensharing,
  onDisconnect,
  onClose,
  disconnecting = false,
}: VncSettingsModalProps) => {
  const i18n = useI18N();
  const overlayRef = useRef<HTMLDivElement>(null);
  // Defaults are reset every time the overlay mounts (no persistence).
  const [version, setVersion] = useState<NoVncVersion>(DEFAULT_VERSION);
  const [swapRB, setSwapRB] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  const texts = {
    title: i18n("modules.machinesRemoteSupportActiveButton.optionsTitle"),
    activeStatus: i18n(
      "modules.machinesRemoteSupportActiveButton.activeStatus",
    ),
    enterScreensharing: i18n(
      "modules.machinesRemoteSupportActiveButton.enterScreensharing",
    ),
    disconnect: i18n("modules.machinesRemoteSupportActiveButton.disconnect"),
    disconnecting: i18n(
      "modules.machinesRemoteSupportActiveButton.disconnecting",
    ),
    vncOptions: i18n("modules.machinesRemoteSupportActiveButton.vncOptions"),
    swapRBLabel: i18n("modules.machinesRemoteSupportActiveButton.swapRBLabel"),
    swapRBTooltip: i18n(
      "modules.machinesRemoteSupportActiveButton.swapRBTooltip",
    ),
    versionLabel: i18n(
      "modules.machinesRemoteSupportActiveButton.vncVersionLabel",
    ),
  };

  const OVERLAY_WIDTH = 340;
  const GAP = 8;
  const VIEWPORT_MARGIN = 8;

  useLayoutEffect(() => {
    const updatePosition = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const overlayHeight = overlayRef.current?.offsetHeight ?? 0;

      const left = Math.max(
        VIEWPORT_MARGIN,
        Math.min(
          rect.right - OVERLAY_WIDTH,
          window.innerWidth - OVERLAY_WIDTH - VIEWPORT_MARGIN,
        ),
      );

      // Prefer opening below the anchor; flip above when there isn't
      // enough room below but there is above (like a tooltip).
      const spaceBelow = window.innerHeight - rect.bottom - GAP;
      const spaceAbove = rect.top - GAP;
      const openAbove = spaceBelow < overlayHeight && spaceAbove > spaceBelow;

      let top = openAbove ? rect.top - GAP - overlayHeight : rect.bottom + GAP;

      // Clamp within the viewport so the overlay is never cut off.
      const maxTop = window.innerHeight - overlayHeight - VIEWPORT_MARGIN;
      top = Math.max(VIEWPORT_MARGIN, Math.min(top, maxTop));

      setPosition({ top, left });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [anchorRef, disconnecting]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (disconnecting) return;
      const target = event.target as Node;
      if (overlayRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (disconnecting) return;
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [anchorRef, onClose, disconnecting]);

  const progress =
    totalSeconds > 0
      ? Math.min(100, Math.max(0, (remainingSeconds / totalSeconds) * 100))
      : 0;

  return createPortal(
    <div
      ref={overlayRef}
      className={styles[cssClassName]}
      style={{ top: position.top, left: position.left, width: OVERLAY_WIDTH }}
      role="dialog"
      aria-modal="false"
      aria-labelledby={`${cssClassName}-title`}
    >
      <h2
        id={`${cssClassName}-title`}
        className={styles[`${cssClassName}__title`]}
      >
        {texts.title}
      </h2>

      <div className={styles[`${cssClassName}__status-card`]}>
        <div className={styles[`${cssClassName}__status-row`]}>
          <span className={styles[`${cssClassName}__status-label`]}>
            <img src={requestRemoteUserIcon} alt="" />
            {texts.activeStatus}
          </span>
          <div
            className={styles[`${cssClassName}__progress`]}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={totalSeconds}
            aria-valuenow={remainingSeconds}
          >
            <div
              className={styles[`${cssClassName}__progress-fill`]}
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className={styles[`${cssClassName}__status-time`]}>
            {formatDuration(remainingSeconds)}
          </span>
        </div>

        <button
          type="button"
          className={styles[`${cssClassName}__enter-button`]}
          onClick={() => onEnterScreensharing({ version, swapRB })}
          disabled={disconnecting}
        >
          {texts.enterScreensharing}
        </button>
      </div>

      <button
        type="button"
        className={styles[`${cssClassName}__disconnect-button`]}
        onClick={onDisconnect}
        disabled={disconnecting}
      >
        {disconnecting ? texts.disconnecting : texts.disconnect}
      </button>

      <div className={styles[`${cssClassName}__divider`]} />

      <div className={styles[`${cssClassName}__options`]}>
        <span className={styles[`${cssClassName}__options-title`]}>
          {texts.vncOptions}
        </span>

        <div className={styles[`${cssClassName}__field`]}>
          <label
            className={styles[`${cssClassName}__field-label`]}
            htmlFor={`${cssClassName}-version`}
          >
            {texts.versionLabel}
          </label>
          <div
            className={styles[`${cssClassName}__dropdown`]}
            aria-disabled={disconnecting}
          >
            <DropDownBlackBackground
              id={`${cssClassName}-version`}
              dropDownValue={version}
              dropDownValuesAndLabels={NOVNC_VERSIONS.reduce<
                Record<string, string>
              >((acc, option) => {
                acc[option] = option;
                return acc;
              }, {})}
              callback={(value) => setVersion(value as NoVncVersion)}
            />
          </div>
        </div>

        <div className={styles[`${cssClassName}__switch-row`]}>
          <CustomTooltip content={texts.swapRBTooltip} maxWidth="280px">
            <span className={styles[`${cssClassName}__switch-label`]}>
              {texts.swapRBLabel}
            </span>
          </CustomTooltip>
          <ToggleButton
            isActive={swapRB}
            handleClick={() => setSwapRB((prev) => !prev)}
            disabled={disconnecting}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
};
